import React, { useCallback, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatBusinessDateTime } from 'config/time';
import FetchFailed from 'reusable/FetchFailed';
import Pagination from 'reusable/pagination';
import TableScroll from 'reusable/TableScroll';
import { useLatestRequest } from 'reusable/useLatestRequest';
import { ConfirmDialog, SuccessNotice, pageToRecover, useOperatorErrors } from 'components/Operator/OperatorUi';
import { endSupportAccess, listSupportAccesses, newRequestId, SUPPORT_PAGE_SIZE, SupportAccess } from './supportApi';
import { SupportStatusBadge, useEndReason } from './supportUi';

interface Props {
  /** この会社のサポートだけ（会社の詳細）。無ければ全部（サポートの一覧）。 */
  company?: number;
  /** 表の名前（見出しの id）。 */
  labelledBy: string;
  /** 一覧を読み直す合図（入った後など。値が替わったら読み直す）。 */
  reloadKey?: number;
}

/**
 * YAC-2026-009 PR7b。運営の画面の、サポートの記録の表と「終わらせる」（GET /v1/operator/support_accesses/・
 * POST …/{id}/end/）。既定は今効いているものだけ（status=active）。「終わったものも表示する」で status=all。
 * どの運営のものも見え、終わらせられる。終わらせる前に確認のダイアログを出す。X-Request-ID は、ダイアログを
 * 開いたときに作り、その中の押し直しでは同じ値を使う（結果が分からない失敗の後も二重にならない）。
 */
const SupportAccessPanel: React.FC<Props> = ({ company, labelledBy, reloadKey = 0 }) => {
  const { t } = useTranslation('layout');
  const errorsOf = useOperatorErrors();
  const endReason = useEndReason();
  const showAllId = useId();
  const [page, setPage] = useState(1);
  const [showAll, setShowAll] = useState(false);
  const [rows, setRows] = useState<SupportAccess[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pending, setPending] = useState<{ access: SupportAccess; requestId: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const beginRequest = useLatestRequest();

  const load = useCallback(async () => {
    const isCurrent = beginRequest();
    setLoading(true);
    try {
      const result = await listSupportAccesses({ page, status: showAll ? 'all' : 'active', company });
      if (!isCurrent()) return;
      const recover = pageToRecover(page, SUPPORT_PAGE_SIZE, { count: result.count, rows: result.results.length });
      if (recover !== null) {
        setPage(recover);
        return;
      }
      setRows(result.results);
      setCount(result.count);
      setLoadError(null);
    } catch (failure) {
      if (!isCurrent()) return;
      const recover = pageToRecover(page, SUPPORT_PAGE_SIZE, { error: failure });
      if (recover !== null) {
        setPage(recover);
        return;
      }
      console.error('Failed to fetch support accesses', failure);
      setLoadError(failure);
    } finally {
      if (isCurrent()) setLoading(false);
    }
    // reloadKey が替わったら読み直す。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beginRequest, company, page, showAll, reloadKey]);

  useEffect(() => {
    load();
  }, [load]);

  const end = async () => {
    if (!pending || busy) return;
    setBusy(true);
    setErrors([]);
    try {
      await endSupportAccess(pending.access.id, pending.requestId);
      setNotice(t('support.ended-notice', { company: pending.access.company.name }));
      setPending(null);
      load();
    } catch (failure) {
      console.error('Failed to end support access', failure);
      setErrors(errorsOf(failure));
    } finally {
      setBusy(false);
    }
  };

  const actionButton = 'rounded-md border border-red-700 px-2 py-1 text-caption text-red-700';

  return (
    <div className="flex flex-col gap-4">
      <SuccessNotice message={notice} onClose={() => setNotice(null)} />
      <div className="flex items-center gap-2">
        <input
          id={showAllId}
          type="checkbox"
          className="h-4 w-4"
          checked={showAll}
          onChange={(event) => {
            setShowAll(event.target.checked);
            setPage(1);
          }}
        />
        <label htmlFor={showAllId} className="text-body-2 text-neutral/300">
          {t('support.show-all')}
        </label>
      </div>
      {loading ? (
        <div role="status" className="py-6 text-center text-neutral/300">
          {t('ui.loading', { ns: 'common' })}
        </div>
      ) : loadError ? (
        <FetchFailed error={loadError} onRetry={load} message={t('support.list-failed')} />
      ) : rows.length === 0 ? (
        <p className="py-6 text-center text-neutral/300">{showAll ? t('support.empty-all') : t('support.empty')}</p>
      ) : (
        <TableScroll labelledBy={labelledBy}>
          <table className="w-full text-left text-body-2 text-neutral/300">
            <thead className="bg-primary/50">
              <tr>
                {company === undefined ? (
                  <th scope="col" className="px-3 py-2 font-medium">{t('support.col-company')}</th>
                ) : null}
                <th scope="col" className="px-3 py-2 font-medium">{t('support.col-operator')}</th>
                <th scope="col" className="px-3 py-2 font-medium">{t('support.col-status')}</th>
                {/* 2026-11-03 UAT A20（和田様）。「サポート開始時刻」「サポート終了時刻」の2つにし、記録の期限（24時間）の列と
                    注記は外した。期限で切れたら、運営の画面からもう一度入ればよい。 */}
                <th scope="col" className="px-3 py-2 font-medium">{t('support.col-created')}</th>
                <th scope="col" className="px-3 py-2 font-medium">{t('support.col-ended')}</th>
                <th scope="col" className="px-3 py-2 font-medium">{t('support.col-actions')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((access) => {
                const reason = endReason(access);
                return (
                  <tr key={access.id} className="border-b">
                    {company === undefined ? <td className="px-3 py-3 font-medium">{access.company.name}</td> : null}
                    <td className="px-3 py-3">{access.operator.name || '-'}</td>
                    <td className="px-3 py-3">
                      <div className="flex flex-col items-start gap-1">
                        <SupportStatusBadge status={access.status} />
                        {reason ? <span className="text-caption">{reason}</span> : null}
                      </div>
                    </td>
                    <td className="px-3 py-3">{formatBusinessDateTime(access.created_at, '-')}</td>
                    <td className="px-3 py-3">{formatBusinessDateTime(access.ended_at, '-')}</td>
                    <td className="px-3 py-3">
                      {access.status === 'active' ? (
                        <button
                          type="button"
                          className={actionButton}
                          aria-label={t('support.end-label', {
                            company: access.company.name,
                            operator: access.operator.name,
                          })}
                          onClick={() => {
                            setErrors([]);
                            setPending({ access, requestId: newRequestId() });
                          }}
                        >
                          {t('support.end')}
                        </button>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableScroll>
      )}
      <Pagination allRows={count} rowsPerPage={SUPPORT_PAGE_SIZE} currentPage={page} onPageChange={setPage} />
      {pending ? (
        <ConfirmDialog
          title={t('support.end-title')}
          body={
            <p>
              {t('support.end-confirm', {
                company: pending.access.company.name,
                operator: pending.access.operator.name,
              })}
            </p>
          }
          confirmLabel={busy ? t('operator.sending') : t('support.end')}
          busy={busy}
          errors={errors}
          danger
          onClose={() => {
            setPending(null);
            setErrors([]);
          }}
          onConfirm={end}
        />
      ) : null}
    </div>
  );
};

export default SupportAccessPanel;
